import { z } from "zod";

export const PrototypeStateValue = z.union([
  z.string(),
  z.number(),
  z.boolean(),
]);
export type PrototypeStateValue = z.infer<typeof PrototypeStateValue>;

/** A list item: a scalar or a flat record of scalars (one level only). */
export const PrototypeItemValue = z.union([
  PrototypeStateValue,
  z.record(z.string(), PrototypeStateValue),
]);
export type PrototypeItemValue = z.infer<typeof PrototypeItemValue>;

export const PrototypeStateEntry = z.union([
  PrototypeStateValue,
  z.array(PrototypeItemValue),
]);
export type PrototypeStateEntry = z.infer<typeof PrototypeStateEntry>;

/** Per-screen slice of initial state values emitted by screen generation. */
export const PrototypeStateSlice = z.record(z.string(), PrototypeStateEntry);
export type PrototypeStateSlice = z.infer<typeof PrototypeStateSlice>;

export type PrototypeItemContext = {
  item?: PrototypeItemValue;
  index: number;
};

export const PrototypeBinOp = z.enum(["+", "-", "*", "/", "%"]);
export type PrototypeBinOp = z.infer<typeof PrototypeBinOp>;

export const PrototypeCmpOp = z.enum(["eq", "neq", "gt", "gte", "lt", "lte"]);
export type PrototypeCmpOp = z.infer<typeof PrototypeCmpOp>;

/** The one value grammar — a condition is just an expr in a boolean context. */
export type PrototypeExpr =
  | PrototypeStateValue
  | { ref: string }
  | { len: string }
  | { item: true | string }
  | { index: true }
  | { bin: PrototypeBinOp; left: PrototypeExpr; right: PrototypeExpr }
  | { concat: PrototypeExpr[] }
  | { cmp: PrototypeCmpOp; left: PrototypeExpr; right: PrototypeExpr }
  | { all: PrototypeExpr[] }
  | { any: PrototypeExpr[] }
  | { not: PrototypeExpr }
  | {
      contains: {
        list: string;
        value?: PrototypeExpr;
        record?: Record<string, PrototypeExpr>;
      };
    }
  | { if: PrototypeExpr; then: PrototypeExpr; else?: PrototypeExpr }
  | { rand: { min: number; max: number } };

export const PrototypeExpr: z.ZodType<PrototypeExpr> = z.lazy(() =>
  z.union([
    PrototypeStateValue,
    z.object({ ref: z.string() }),
    z.object({ len: z.string() }),
    z.object({ item: z.union([z.literal(true), z.string()]) }),
    z.object({ index: z.literal(true) }),
    z.object({
      bin: PrototypeBinOp,
      left: PrototypeExpr,
      right: PrototypeExpr,
    }),
    z.object({ concat: z.array(PrototypeExpr) }),
    z.object({
      cmp: PrototypeCmpOp,
      left: PrototypeExpr,
      right: PrototypeExpr,
    }),
    z.object({ all: z.array(PrototypeExpr).min(2) }),
    z.object({ any: z.array(PrototypeExpr).min(2) }),
    z.object({ not: PrototypeExpr }),
    z.object({
      contains: z.object({
        list: z.string(),
        value: PrototypeExpr.optional(),
        record: z.record(z.string(), PrototypeExpr).optional(),
      }),
    }),
    z.object({
      if: PrototypeExpr,
      then: PrototypeExpr,
      else: PrototypeExpr.optional(),
    }),
    z.object({ rand: z.object({ min: z.number(), max: z.number() }) }),
  ]),
);

/**
 * How state is read: handlers read live, render-time projection reads a
 * tracked snapshot, normalise reads the initial slice — same evaluator.
 */
export type PrototypeStateReader = (
  key: string,
) => PrototypeStateEntry | undefined;

export const isPrototypeScalar = (v: unknown): v is PrototypeStateValue =>
  typeof v === "string" || typeof v === "number" || typeof v === "boolean";

/** Render an evaluated value; `undefined` (and anything non-scalar) → "". */
export const toDisplayText = (value: unknown): string =>
  isPrototypeScalar(value) ? String(value) : "";

const asNumber = (v: PrototypeStateValue | undefined): number | undefined =>
  typeof v === "number" ? v : undefined;

/**
 * The single evaluator. Returns a scalar or `undefined`; boolean contexts
 * coerce with `Boolean(...)`, so missing state keys read falsy. `undefined`
 * operands poison arithmetic to `undefined` and compare as false.
 */
export const resolveExpr = (
  expr: PrototypeExpr,
  read: PrototypeStateReader,
  ctx?: PrototypeItemContext,
): PrototypeStateValue | undefined => {
  if (isPrototypeScalar(expr)) return expr;
  if ("ref" in expr) {
    const value = read(expr.ref);
    return Array.isArray(value) ? undefined : value;
  }
  if ("len" in expr) {
    const value = read(expr.len);
    if (Array.isArray(value) || typeof value === "string") return value.length;
    return 0;
  }
  if ("item" in expr) {
    const item = ctx?.item;
    if (item === undefined) return undefined;
    if (expr.item === true) {
      return typeof item === "object" ? undefined : item;
    }
    return typeof item === "object" ? item[expr.item] : undefined;
  }
  if ("index" in expr) return ctx?.index;
  if ("bin" in expr) {
    const left = resolveExpr(expr.left, read, ctx);
    const right = resolveExpr(expr.right, read, ctx);
    if (left === undefined || right === undefined) return undefined;
    if (expr.bin === "+") {
      if (typeof left === "number" && typeof right === "number") {
        return left + right;
      }
      if (typeof left === "string" || typeof right === "string") {
        return `${left}${right}`;
      }
      return undefined;
    }
    const a = asNumber(left);
    const b = asNumber(right);
    if (a === undefined || b === undefined) return undefined;
    switch (expr.bin) {
      case "-":
        return a - b;
      case "*":
        return a * b;
      case "/":
        return b === 0 ? undefined : a / b;
      case "%":
        // positive modulo, so grid wrap-around works on negatives
        return b === 0 ? undefined : ((a % b) + b) % b;
    }
  }
  if ("concat" in expr) {
    return expr.concat
      .map((part) => toDisplayText(resolveExpr(part, read, ctx)))
      .join("");
  }
  if ("cmp" in expr) {
    const left = resolveExpr(expr.left, read, ctx);
    const right = resolveExpr(expr.right, read, ctx);
    if (expr.cmp === "eq") return left === right;
    if (expr.cmp === "neq") return left !== right;
    // ordering is numeric-only; anything else compares false
    if (typeof left !== "number" || typeof right !== "number") return false;
    switch (expr.cmp) {
      case "gt":
        return left > right;
      case "gte":
        return left >= right;
      case "lt":
        return left < right;
      case "lte":
        return left <= right;
    }
  }
  if ("all" in expr) {
    return expr.all.every((e) => Boolean(resolveExpr(e, read, ctx)));
  }
  if ("any" in expr) {
    return expr.any.some((e) => Boolean(resolveExpr(e, read, ctx)));
  }
  if ("not" in expr) return !resolveExpr(expr.not, read, ctx);
  if ("contains" in expr) {
    const list = read(expr.contains.list);
    if (!Array.isArray(list)) return false;
    const record = expr.contains.record;
    if (record !== undefined) {
      const resolved = Object.entries(record).map(
        ([key, value]) => [key, resolveExpr(value, read, ctx)] as const,
      );
      return list.some(
        (entry) =>
          typeof entry === "object" &&
          entry !== null &&
          resolved.every(([key, value]) => entry[key] === value),
      );
    }
    if (expr.contains.value === undefined) return false;
    const value = resolveExpr(expr.contains.value, read, ctx);
    return list.some((entry) => entry === value);
  }
  if ("if" in expr) {
    if (resolveExpr(expr.if, read, ctx)) {
      return resolveExpr(expr.then, read, ctx);
    }
    return expr.else === undefined
      ? undefined
      : resolveExpr(expr.else, read, ctx);
  }
  if ("rand" in expr) {
    const { min, max } = expr.rand;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
  return undefined;
};

/** State keys an expr reads (`ref`/`len`/`contains.list`), for bake checks. */
export const collectExprStateKeys = (expr: PrototypeExpr): string[] => {
  if (isPrototypeScalar(expr)) return [];
  if ("ref" in expr) return [expr.ref];
  if ("len" in expr) return [expr.len];
  if ("item" in expr || "index" in expr || "rand" in expr) return [];
  if ("bin" in expr || "cmp" in expr) {
    return [
      ...collectExprStateKeys(expr.left),
      ...collectExprStateKeys(expr.right),
    ];
  }
  if ("concat" in expr) return expr.concat.flatMap(collectExprStateKeys);
  if ("all" in expr) return expr.all.flatMap(collectExprStateKeys);
  if ("any" in expr) return expr.any.flatMap(collectExprStateKeys);
  if ("not" in expr) return collectExprStateKeys(expr.not);
  if ("contains" in expr) {
    return [
      expr.contains.list,
      ...(expr.contains.value ? collectExprStateKeys(expr.contains.value) : []),
      ...Object.values(expr.contains.record ?? {}).flatMap(
        collectExprStateKeys,
      ),
    ];
  }
  if ("if" in expr) {
    return [
      ...collectExprStateKeys(expr.if),
      ...collectExprStateKeys(expr.then),
      ...(expr.else ? collectExprStateKeys(expr.else) : []),
    ];
  }
  return [];
};

/**
 * `unit` suffixes bare numbers only; computed strings ("70%") pass through.
 * Non-scalar/boolean results → undefined (the style prop is skipped).
 */
export const applyStyleUnit = (
  value: PrototypeStateValue | undefined,
  unit?: string,
): string | number | undefined => {
  if (typeof value === "number") return unit ? `${value}${unit}` : value;
  if (typeof value === "string") return value;
  return undefined;
};
